/* What the backend charges to upgrade a building: the ladder in `utils/ladder` (requirement 34
   — the price doubles from the second level up, and it is `level_up_building`'s arithmetic).

   One implementation, because a balance rule written out twice is a balance rule that drifts. The
   numbers themselves are never duplicated here — they come from `GET /game-config/`, which is the
   same `server_config.py` the server charges from. */

import { buildingCostMultiplier } from "src/utils/gameModifiers"
import { upgradeCost } from "src/utils/ladder"

export const upgradeCostFor = ({ gameConfig, team, buildingCode, level, race }) => {
  if (!gameConfig || !team) return null
  const configKey = team === "human" ? "human_config" : "zombie_config"
  const building = gameConfig[configKey]?.BUILDINGS?.[buildingCode]
  if (!building) return null
  // `level_up_building` charges `int(upgrade_cost(level, A, B) * building_cost_multiplier(...))`, and the
  // Infestor's -25% building price lives in that multiplier. Quoting the list price to a race that
  // does not pay it is the same wrong-number bug as the ETA and the hatchery capacity.
  const base = upgradeCost(level, building.UPGRADE_A, building.UPGRADE_B)
  return Math.trunc(base * buildingCostMultiplier({ gameConfig, race }))
}

// What the upgrade is paid in — the number alone misled a player into reading a worker price as
// food (owner's report P3, 2026-09-01): humans pay with the city's own workers, zombies with meat.
export const UPGRADE_CURRENCY_ICON = { human: "👷", zombie: "🥩" }

/* The same gates `level_up_building` checks, answered before the click instead of after it
   (owner's report P2): a control that will be refused must be disabled with the reason on it.
   The main-building ceiling is gone (owner's Q36, 2026-09-03) — a city may specialise, a
   putrefactor above its hatchery included, so the only gates left are the resources and the
   one-a-round rule. Returns { cost, allowed, reason } where reason is null or one of "ordered" |
   "workers" | "meat" — the caller turns it into words. */
export const upgradeGate = ({
  gameConfig,
  team,
  buildingCode,
  level,
  race,
  workersOnCell,
  food,
  ordered = false,
  held = 0,
}) => {
  const cost = upgradeCostFor({ gameConfig, team, buildingCode, level, race })
  if (cost == null) return { cost, allowed: false, reason: null }
  // One level a building a round (owner, 2026-10-02): the order lands when the round turns, and
  // `level_up_building` refuses a second one (`BuildingAlreadyUpgrading`). `held` is what the
  // orders already given will take from the same purse (`utils/pendingOrders`).
  if (ordered) return { cost, allowed: false, reason: "ordered" }
  if (team === "human" && cost + held > (workersOnCell ?? 0)) {
    return { cost, allowed: false, reason: "workers" }
  }
  if (team === "zombie" && cost + held > (food ?? 0)) {
    return { cost, allowed: false, reason: "meat" }
  }
  return { cost, allowed: true, reason: null }
}

export default upgradeCostFor
